import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  HardDrive,
  RefreshCw,
  AlertTriangle,
  Database,
  Layers,
  Activity,
  Check,
  Copy,
  Info,
  Search,
  Sparkles,
  Terminal,
  Table,
  TableOfContents,
  Gauge,
  ChevronDown,
  ShieldCheck,
  Clock,
} from 'lucide-react';
import baseapi from '../../../services/baseapi';
import { CustomDropdown } from '../../../components/CustomDropdown ';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  CartesianGrid,
  Legend,
  ScatterChart,
  Scatter,
  ZAxis,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from 'recharts';

export interface MariaDbStorageProps {
  activeCluster: string;
  timeRange?: string;
  clientId?: string;
  refreshKey?: number;
  dbType?: string;
}

export interface MariaDbKPIs {
  total_size_mb?: number | null;
  data_mb?: number | null;
  index_mb?: number | null;
  reclaimable_mb?: number | null;
  tables?: number | null;
  indexes?: number | null;
  auto_increment_max_pct?: number | null;
  open_alerts?: {
    total?: number | null;
    warnings?: number | null;
    critical?: number | null;
  };
}

export interface MariaDbEngineItem {
  engine: string;
  tables?: number | null;
  data_mb?: number | null;
  index_mb?: number | null;
  free_mb?: number | null;
  est_rows?: number | null;
  databases?: string[];
  size_mb?: number | null;
  size_pct?: number | null;
}

export interface MariaDbDatabaseItem {
  database: string;
  is_system?: boolean;
  size_mb?: number | null;
  data_mb?: number | null;
  index_mb?: number | null;
  free_mb?: number | null;
  tables?: number | null;
  views?: number | null;
  row_estimate?: number | null;
  size_source?: string;
  engines?: Array<{
    engine: string;
    data_mb?: number | null;
    index_mb?: number | null;
    free_mb?: number | null;
    est_rows?: number | null;
    tables?: number | null;
  }>;
  fragmented_tables?: number | null;
  tables_without_pk?: number | null;
  auto_increment_max_pct?: number | null;
  index_count?: number | null;
  ignored_indexes?: number | null;
  unused_indexes?: number | null;
  redundant_indexes?: number | null;
  stats_missing_tables?: number | null;
  maint_checked_at?: string | null;
  status?: string;
  status_reasons?: Array<{ severity: string; reason: string }>;
}

export interface MariaDbLargestTableItem {
  table: string;
  database: string;
  engine: string;
  data_mb?: number | null;
  index_mb?: number | null;
  free_mb?: number | null;
  est_rows?: number | null;
  avg_row_bytes?: number | null;
  auto_increment?: number | null;
  row_format?: string | null;
  collation?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  stats_rows?: number | null;
  stats_updated_at?: string | null;
}

export interface MariaDbIndexItem {
  table: string;
  index: string;
  database?: string;
  columns?: string[];
  type?: string;
  unique?: boolean;
  cardinality?: number | null;
  reads?: number | null;
  writes?: number | null;
  size_mb?: number | null;
  unused?: boolean;
  redundant_of?: string;
}

export interface MariaDbFindingItem {
  severity: 'critical' | 'warning' | 'info' | string;
  category: string;
  database?: string | null;
  object?: string | null;
  finding: string;
  impact?: string | null;
  remediation?: string | null;
  command?: string | null;
}

export interface MariaDbFindingsData {
  note?: string;
  count?: number;
  by_severity?: Record<string, number>;
  items?: MariaDbFindingItem[];
}

export interface MariaDbStorageOverviewData {
  status?: boolean;
  applicable?: boolean;
  db_type?: string;
  kpis?: MariaDbKPIs;
  engines?: MariaDbEngineItem[];
  databases?: MariaDbDatabaseItem[];
  largest_tables?: MariaDbLargestTableItem[];
  fragmentation?: {
    tables?: number | null;
    reclaimable_mb?: number | null;
    list?: any[];
    note?: string;
  };
  auto_increment?: {
    columns?: number | null;
    max_used_pct?: number | null;
    list?: any[];
    available?: boolean;
    reason?: string;
  };
  indexes?: {
    total?: number | null;
    ignored?: number | null;
    unused?: number | null;
    redundant?: number | null;
    usage_source?: string | null;
    usage_since?: string | null;
    ignored_list?: any[];
    unused_list?: MariaDbIndexItem[];
    redundant_list?: MariaDbIndexItem[];
    definitions?: MariaDbIndexItem[];
    note?: string;
  };
  tables_without_pk?: {
    count?: number | null;
    list?: Array<{
      database: string;
      table: string;
      engine: string;
    }>;
    note?: string;
  };
  statistics?: {
    missing_persistent_stats?: number | null;
  };
  binlog?: {
    enabled?: boolean;
    files?: number | null;
    size_mb?: number | null;
    expire_sec?: number | null;
  };
  innodb?: {
    history_list_length?: number | null;
    system_tablespace_mb?: number | null;
    file_per_table?: boolean;
  };
  temp_tables_to_disk_pct?: number | null;
  coverage?: {
    databases?: number | null;
    databases_checked?: number | null;
    oldest_check?: string | null;
    interval_hours?: number | null;
    unavailable?: Record<string, any>;
  };
  events?: Array<{
    time?: string;
    database?: string;
    event?: string;
    severity?: string;
    details?: string;
  }>;
  thresholds?: {
    auto_increment_warn_pct?: number;
    auto_increment_critical_pct?: number;
    fragmented_min_free_mb?: number;
    fragmented_min_pct?: number;
    history_list_warn?: number;
    temp_tables_to_disk_warn_pct?: number;
  };
  findings?: MariaDbFindingsData;
  [key: string]: any;
}

type TableExplorerTab = 'inventory' | 'tables' | 'indexes' | 'findings' | 'events';

// Colors exactly matching PostgresStorageView
const CHART_PALETTE = ['#3b82f6', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#64748b'];

const formatSize = (mb: number | null | undefined): string => {
  if (mb === null || mb === undefined || isNaN(mb)) return '—';
  if (mb >= 1024) {
    return `${(mb / 1024).toLocaleString(undefined, { maximumFractionDigits: 2 })} GB`;
  }
  return `${mb.toLocaleString(undefined, { maximumFractionDigits: 2 })} MB`;
};

const formatPct = (pct: number | null | undefined): string => {
  if (pct === null || pct === undefined || isNaN(pct)) return '—';
  return `${pct.toFixed(2)}%`;
};

const formatNumber = (num: number | null | undefined, decimals = 0): string => {
  if (num === null || num === undefined || isNaN(num)) return '0';
  return Number(num).toLocaleString(undefined, { maximumFractionDigits: decimals });
};

const formatDateTime = (dateStr: string | undefined | null): string => {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? dateStr : d.toLocaleString();
  } catch {
    return dateStr;
  }
};

const ENGINE_COLORS: Record<string, string> = {
  InnoDB: '#3b82f6', // Blue
  Aria: '#06b6d4', // Cyan
  CSV: '#64748b', // Slate
  MyISAM: '#8b5cf6', // Violet
  MEMORY: '#10b981', // Emerald
};

export const MariaDbStorage: React.FC<MariaDbStorageProps> = ({
  activeCluster,
  timeRange = '60',
  clientId,
  refreshKey,
  dbType = 'MARIADB',
}) => {
  const [data, setData] = useState<MariaDbStorageOverviewData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedText, setCopiedText] = useState<string | null>(null);

  // Database Filter & Table Explorer State
  const [selectedDb, setSelectedDb] = useState<string>('all');
  const [activeTableTab, setActiveTableTab] = useState<TableExplorerTab>('inventory');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [tableEngineFilter, setTableEngineFilter] = useState<string>('all');
  const [indexFilter, setIndexFilter] = useState<'all' | 'unused' | 'redundant'>('all');
  const [expandedDb, setExpandedDb] = useState<string | null>(null);

  const handleCopy = (text: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedText(text);
    setTimeout(() => setCopiedText(null), 1800);
  };

  // Theme Detection for Dynamic Chart Colors in Light & Dark Mode
  const [isDark, setIsDark] = useState<boolean>(() =>
    typeof document !== 'undefined' ? document.documentElement.classList.contains('dark') : false
  );

  useEffect(() => {
    const checkDark = () => setIsDark(document.documentElement.classList.contains('dark'));
    checkDark();
    const observer = new MutationObserver(checkDark);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const chartTheme = useMemo(() => {
    return {
      grid: isDark ? '#374151' : '#e2e8f0',
      gridOpacity: isDark ? 0.35 : 0.8,
      axisStroke: isDark ? '#4b5563' : '#cbd5e1',
      axisText: isDark ? '#9ca3af' : '#475569',
      radarWeb: isDark ? '#64748b' : '#94a3b8',
      radarAngleText: isDark ? '#f1f5f9' : '#1e293b',
      radarRadiusTick: isDark ? '#94a3b8' : '#64748b',
      pieStroke: isDark ? '#1f2937' : '#ffffff',
      tooltip: {
        backgroundColor: isDark ? 'rgba(17, 24, 39, 0.95)' : '#ffffff',
        borderColor: isDark ? '#374151' : '#e2e8f0',
        borderRadius: '8px',
        fontSize: '11px',
        color: isDark ? '#ffffff' : '#0f172a',
        boxShadow: isDark
          ? '0 10px 15px -3px rgba(0, 0, 0, 0.5), 0 4px 6px -4px rgba(0, 0, 0, 0.5)'
          : '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
      },
      tooltipItem: {
        color: isDark ? '#ffffff' : '#0f172a',
      },
      tooltipLabel: {
        color: isDark ? '#ffffff' : '#0f172a',
        fontWeight: 600,
      },
    };
  }, [isDark]);

  const fetchData = useCallback(async () => {
    if (!activeCluster) return;
    setLoading(true);
    setError(null);

    const minutes = parseInt(timeRange || '60', 10);
    const params: any = {
      cluster_id: activeCluster,
      minutes: isNaN(minutes) ? 60 : minutes,
    };
    if (clientId) {
      params.client_id = clientId;
    }

    try {
      const res = await baseapi.get(
        '/db_monitor_dashboard/mariadb/storage-maintenance/overview',
        { params }
      );
      const resData: MariaDbStorageOverviewData = res.data?.data || res.data;
      setData(resData);
    } catch (err: any) {
      console.warn('MariaDbStorage fetch error:', err);
      setError(
        err?.response?.data?.message || err?.message || 'Failed to load MariaDB storage overview'
      );
    } finally {
      setLoading(false);
    }
  }, [activeCluster, timeRange, clientId]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  const kpis = data?.kpis;
  const engines = data?.engines || [];
  const databases = data?.databases || [];
  const largestTables = data?.largest_tables || [];
  const indexes = data?.indexes;
  const findings = data?.findings;
  const coverage = data?.coverage;
  const tablesWithoutPk = data?.tables_without_pk;
  const innodb = data?.innodb;
  const binlog = data?.binlog;
  const tempTablesPct = data?.temp_tables_to_disk_pct ?? 0;
  const events = data?.events || [];

  // Database dropdown options
  const dbDropdownOptions = useMemo(() => {
    const list = databases.map((db) => ({
      label: `${db.database} (${formatSize(db.size_mb)})`,
      value: db.database,
    }));
    return [{ label: 'All Databases', value: 'all' }, ...list];
  }, [databases]);

  // Overall status matching PostgresStorageView
  const overallStatus = useMemo(() => {
    const crit = kpis?.open_alerts?.critical ?? 0;
    const warn = kpis?.open_alerts?.warnings ?? 0;
    if (crit > 0) return { label: 'CRITICAL', color: 'rose' };
    if (warn > 0) return { label: `${warn} WARNING${warn > 1 ? 'S' : ''}`, color: 'amber' };
    return { label: 'ALL HEALTHY', color: 'emerald' };
  }, [kpis]);

  // Chart 1: Engine distribution (Donut Pie) - filtered to non-zero values for bulletproof Recharts rendering
  const enginePieData = useMemo(() => {
    return engines
      .map((e, i) => {
        const val = Number((e.size_mb || (e.data_mb || 0) + (e.index_mb || 0)).toFixed(3));
        return {
          name: e.engine,
          value: Math.max(val, 0.001),
          displayVal: val,
          percentage: e.size_pct ?? 0,
          tables: e.tables ?? 0,
          color: ENGINE_COLORS[e.engine] || CHART_PALETTE[i % CHART_PALETTE.length],
        };
      })
      .filter((e) => e.tables > 0 || e.displayVal > 0);
  }, [engines]);

  // Chart 2: Footprint by database (stacked horizontal bars matching PostgresStorageView Largest Relations)
  const dbFootprintData = useMemo(() => {
    let list = [...databases].sort((a, b) => (b.size_mb || 0) - (a.size_mb || 0));
    if (selectedDb !== 'all') {
      list = databases.filter((d) => d.database.toLowerCase() === selectedDb.toLowerCase());
    }
    return list.slice(0, 7).map((db) => {
      const dataMb = Number((db.data_mb || 0).toFixed(3));
      const indexMb = Number((db.index_mb || 0).toFixed(3));
      const freeMb = Number((db.free_mb || 0).toFixed(3));
      const totalMb = Math.max(Number((db.size_mb || 0).toFixed(3)), dataMb + indexMb + freeMb, 0.001);
      return {
        database: db.database,
        size_mb: totalMb,
        data_mb: dataMb,
        index_mb: indexMb,
        free_mb: freeMb,
      };
    });
  }, [databases, selectedDb]);



  // Chart 3: Largest tables data vs index (Vertical BarChart with explicit numeric container height)
  const largestTablesChartData = useMemo(() => {
    let list = largestTables;
    if (selectedDb !== 'all') {
      list = largestTables.filter((t) => t.database.toLowerCase() === selectedDb.toLowerCase());
    }
    return list.slice(0, 8).map((t) => ({
      name: t.table.length > 13 ? `${t.table.slice(0, 11)}..` : t.table,
      fullName: `${t.database}.${t.table}`,
      data_mb: Number((t.data_mb || 0).toFixed(3)),
      index_mb: Number((t.index_mb || 0).toFixed(3)),
      engine: t.engine,
    }));
  }, [largestTables, selectedDb]);

  // Chart 4: Row size vs row count (Scatter / Bubble)
  const bubbleData = useMemo(() => {
    let list = largestTables;
    if (selectedDb !== 'all') {
      list = largestTables.filter((t) => t.database.toLowerCase() === selectedDb.toLowerCase());
    }
    return list.map((t) => ({
      name: t.table,
      engine: t.engine,
      x: t.est_rows || 1,
      y: t.avg_row_bytes || 1,
      z: Math.max(1, t.data_mb || 0.1),
    }));
  }, [largestTables, selectedDb]);

  const ariaScatterData = useMemo(() => bubbleData.filter((b) => b.engine === 'Aria'), [bubbleData]);
  const innodbScatterData = useMemo(() => bubbleData.filter((b) => b.engine === 'InnoDB'), [bubbleData]);

  // Chart 5: Database health radar data
  const radarSubjectDb = useMemo(() => {
    if (selectedDb !== 'all') {
      return databases.find((d) => d.database.toLowerCase() === selectedDb.toLowerCase()) || databases[0];
    }
    return databases.find((d) => d.status === 'warning' || d.status === 'critical') || databases[0];
  }, [databases, selectedDb]);

  const radarChartData = useMemo(() => {
    if (!radarSubjectDb) return [];
    return [
      { metric: 'No PK', target: radarSubjectDb.tables_without_pk || 0, baseline: 0, fullMark: 2 },
      { metric: 'Unused idx', target: radarSubjectDb.unused_indexes || 0, baseline: 0, fullMark: 4 },
      { metric: 'Redundant idx', target: radarSubjectDb.redundant_indexes || 0, baseline: 0, fullMark: 2 },
      { metric: 'Fragmented', target: radarSubjectDb.fragmented_tables || 0, baseline: 0, fullMark: 4 },
      { metric: 'Stats missing', target: radarSubjectDb.stats_missing_tables || 0, baseline: 0, fullMark: 2 },
      { metric: 'Reclaimable MB', target: Number((radarSubjectDb.free_mb || 0).toFixed(1)), baseline: 0, fullMark: 10 },
    ];
  }, [radarSubjectDb]);

  // Chart 6: Tables per database (Simple Radial Bar Chart)
  const tablesPerDbData = useMemo(() => {
    let list = [...databases].sort((a, b) => (b.tables || 0) - (a.tables || 0));
    if (selectedDb !== 'all') {
      list = databases.filter((d) => d.database.toLowerCase() === selectedDb.toLowerCase());
    }
    return list.slice(0, 6).map((d) => ({
      name: d.database,
      tables: d.tables || 0,
      is_system: d.is_system,
    }));
  }, [databases, selectedDb]);

  const tablesPieData = useMemo(() => {
    return tablesPerDbData
      .filter((d) => (d.tables || 0) > 0)
      .map((d, i) => ({
        name: d.name,
        value: d.tables || 0,
        color: CHART_PALETTE[i % CHART_PALETTE.length],
      }));
  }, [tablesPerDbData]);


  // Chart 7: Index reads vs activity (Horizontal BarChart)
  const indexReadsListData = useMemo(() => {
    const list = [
      ...(indexes?.definitions || []),
      ...(indexes?.unused_list || []),
      ...(indexes?.redundant_list || []),
    ];
    let filtered = list;
    if (selectedDb !== 'all') {
      filtered = list.filter((i) => (i.database || '').toLowerCase() === selectedDb.toLowerCase());
    }
    const seen = new Set<string>();
    const uniqueList: MariaDbIndexItem[] = [];
    for (const item of filtered) {
      const key = `${item.table}.${item.index}`;
      if (!seen.has(key)) {
        seen.add(key);
        uniqueList.push(item);
      }
    }
    return uniqueList
      .sort((a, b) => (b.reads || 0) - (a.reads || 0))
      .slice(0, 7)
      .map((ix) => ({
        name: ix.index.length > 15 ? `${ix.index.slice(0, 13)}..` : ix.index,
        fullName: `${ix.table}.${ix.index}`,
        index: ix.index,
        table: ix.table,
        reads: ix.reads || 0,
        size_mb: Number((ix.size_mb || 0).toFixed(3)),
        unused: (ix.reads || 0) === 0,
        fill: (ix.reads || 0) === 0 ? '#f59e0b' : '#3b82f6',
      }));
  }, [indexes, selectedDb]);


  // Filtered lists for Table Explorer
  const filteredDatabases = useMemo(() => {
    return databases.filter((db) => {
      const matchDb = selectedDb === 'all' || db.database.toLowerCase() === selectedDb.toLowerCase();
      const matchSearch =
        !searchQuery.trim() ||
        db.database.toLowerCase().includes(searchQuery.toLowerCase()) ||
        db.status?.toLowerCase().includes(searchQuery.toLowerCase());
      return matchDb && matchSearch;
    });
  }, [databases, selectedDb, searchQuery]);

  const filteredTables = useMemo(() => {
    return largestTables.filter((t) => {
      const matchDb = selectedDb === 'all' || t.database.toLowerCase() === selectedDb.toLowerCase();
      const matchEngine =
        tableEngineFilter === 'all' || t.engine.toLowerCase() === tableEngineFilter.toLowerCase();
      const matchSearch =
        !searchQuery.trim() ||
        t.table.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.database.toLowerCase().includes(searchQuery.toLowerCase());
      return matchDb && matchEngine && matchSearch;
    });
  }, [largestTables, selectedDb, tableEngineFilter, searchQuery]);

  const filteredIndexes = useMemo(() => {
    let list: (MariaDbIndexItem & { flag?: string })[] = [];
    if (indexFilter === 'unused') {
      list = (indexes?.unused_list || []).map((x) => ({ ...x, flag: 'unused' }));
    } else if (indexFilter === 'redundant') {
      list = (indexes?.redundant_list || []).map((x) => ({ ...x, flag: 'redundant' }));
    } else {
      const unusedKeys = new Set((indexes?.unused_list || []).map((x) => `${x.table}.${x.index}`));
      const redundantKeys = new Set((indexes?.redundant_list || []).map((x) => `${x.table}.${x.index}`));
      list = (indexes?.definitions || []).map((ix) => {
        const key = `${ix.table}.${ix.index}`;
        let flag = '';
        if (unusedKeys.has(key)) flag = 'unused';
        else if (redundantKeys.has(key)) flag = 'redundant';
        else if (ix.unique) flag = 'unique';
        return { ...ix, flag };
      });
    }

    return list.filter((ix) => {
      const matchDb =
        selectedDb === 'all' || !ix.database || ix.database.toLowerCase() === selectedDb.toLowerCase();
      const matchSearch =
        !searchQuery.trim() ||
        ix.index.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ix.table.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (ix.columns && ix.columns.some((c) => c.toLowerCase().includes(searchQuery.toLowerCase())));
      return matchDb && matchSearch;
    });
  }, [indexes, indexFilter, selectedDb, searchQuery]);

  const filteredFindings = useMemo(() => {
    const items = findings?.items || [];
    return items.filter((f) => {
      const matchDb =
        selectedDb === 'all' || !f.database || f.database.toLowerCase() === selectedDb.toLowerCase();
      const matchSearch =
        !searchQuery.trim() ||
        f.finding.toLowerCase().includes(searchQuery.toLowerCase()) ||
        f.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (f.object && f.object.toLowerCase().includes(searchQuery.toLowerCase()));
      return matchDb && matchSearch;
    });
  }, [findings, selectedDb, searchQuery]);

  const filteredEvents = useMemo(() => {
    return events.filter((ev) => {
      const matchDb =
        selectedDb === 'all' || !ev.database || ev.database.toLowerCase() === selectedDb.toLowerCase();
      const matchSearch =
        !searchQuery.trim() ||
        (ev.event && ev.event.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (ev.details && ev.details.toLowerCase().includes(searchQuery.toLowerCase()));
      return matchDb && matchSearch;
    });
  }, [events, selectedDb, searchQuery]);

  const isTempWarn = tempTablesPct > 25;

  // Render Loading Skeleton matching PostgresStorageView
  if (loading && !data) {
    return (
      <div className="space-y-4">
        <div className="h-14 rounded-xl bg-gray-100 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 animate-pulse" />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2.5">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="h-28 rounded-xl bg-gray-100 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 animate-pulse" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3.5">
          <div className="lg:col-span-4 h-64 rounded-xl bg-gray-100 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 animate-pulse" />
          <div className="lg:col-span-8 h-64 rounded-xl bg-gray-100 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 animate-pulse" />
        </div>
        <div className="h-96 rounded-xl bg-gray-100 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 animate-pulse" />
      </div>
    );
  }

  // Render Error State matching PostgresStorageView
  if (error && !data) {
    return (
      <div className="p-8 rounded-xl bg-rose-50 dark:bg-rose-950/20 border border-rose-200 dark:border-rose-900/40 text-center space-y-4">
        <AlertTriangle className="w-10 h-10 text-rose-500 mx-auto" />
        <div>
          <h3 className="text-base font-semibold text-rose-900 dark:text-rose-200">
            Failed to Load MariaDB Storage &amp; Maintenance Overview
          </h3>
          <p className="text-sm text-rose-600 dark:text-rose-400 mt-1 max-w-lg mx-auto">{error}</p>
        </div>
        <button
          onClick={() => fetchData()}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold shadow-sm transition-colors cursor-pointer"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3.5">
      {/* ─────────────────────────────────────────────────────────────
          ROW 0: TOP BANNER HEADER & CONTROLS (Theme from PostgresStorageView)
          ───────────────────────────────────────────────────────────── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-2.5 pb-3 border-b border-gray-200 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <div className="p-1.5 rounded-lg bg-orange-500 text-white shadow-sm">
            <HardDrive className="w-4 h-4" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">
                Storage &amp; Maintenance
              </h2>
              <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-blue-50 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-500/30">
                {(dbType || 'MARIADB').toUpperCase()}
              </span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-200 mt-0.5">
              {coverage?.databases_checked ?? databases.length ?? 0} databases · {kpis?.tables ?? 0} tables
              {coverage?.oldest_check ? ` · last check ${formatDateTime(coverage.oldest_check)}` : ''}
              {coverage?.interval_hours ? ` · hourly` : ''}
            </p>
          </div>
        </div>

        {/* Controls: Status pill, Database Selector, Findings Badge, Reset & Refresh */}
        <div className="flex items-center flex-wrap gap-2.5">
          {/* Status Badge */}
          <span
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border shadow-sm ${overallStatus.color === 'rose'
              ? 'bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-300 dark:border-rose-500/30'
              : overallStatus.color === 'amber'
                ? 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-300 dark:border-amber-500/30'
                : 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-300 dark:border-emerald-500/30'
              }`}
          >
            <span
              className={`w-2 h-2 rounded-full ${overallStatus.color === 'rose'
                ? 'bg-rose-500 animate-pulse'
                : overallStatus.color === 'amber'
                  ? 'bg-amber-500'
                  : 'bg-emerald-500'
                }`}
            />
            {overallStatus.label}
          </span>

          {/* Findings Badge Pill */}
          {findings?.count != null && findings.count > 0 && (
            <button
              onClick={() => {
                setActiveTableTab('findings');
                document.getElementById('table-explorer-section')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border shadow-sm bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-300 dark:border-amber-500/30 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors cursor-pointer"
            >
              <Sparkles className="w-3.5 h-3.5 text-amber-500" />
              {findings.count} {findings.count === 1 ? 'Finding' : 'Findings'}
            </button>
          )}

          {/* Database Selector Dropdown */}
          <CustomDropdown
            data={dbDropdownOptions}
            selectedValue={selectedDb}
            onSelect={(val) => setSelectedDb(val)}
            width="w-48 sm:w-56"
            placeholder="Select database"
            buttonHeight={8}
          />

          {/* Reset Button */}
          {selectedDb !== 'all' && (
            <button
              onClick={() => {
                setSelectedDb('all');
                setSearchQuery('');
              }}
              className="px-2.5 py-1.5 rounded-lg text-xs font-semibold text-gray-700 dark:text-gray-300 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-700 transition-colors cursor-pointer"
            >
              Reset
            </button>
          )}

        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          ROW 1: 6 TOP KPI CARDS (Matching PostgresStorageView & Prototype)
          ───────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
        {/* KPI 1: Total Size */}
        <div className="p-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="text-[11px] font-medium text-gray-500 dark:text-gray-200 flex items-center justify-between">
            <span>Total size</span>
            <Database className="w-3.5 h-3.5 text-blue-500" />
          </div>
          <div className="text-xl font-bold text-gray-900 dark:text-white font-mono tracking-tight my-1">
            {formatSize(kpis?.total_size_mb)}
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-200 truncate">
            data {formatSize(kpis?.data_mb)} · index {formatSize(kpis?.index_mb)}
          </div>
        </div>

        {/* KPI 2: Reclaimable */}
        <div className="p-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="text-[11px] font-medium text-gray-500 dark:text-gray-200 flex items-center justify-between">
            <span>Reclaimable</span>
            <HardDrive className="w-3.5 h-3.5 text-amber-500" />
          </div>
          <div className="text-xl font-bold text-amber-600 dark:text-amber-400 font-mono tracking-tight my-1">
            {formatSize(kpis?.reclaimable_mb ?? data?.fragmentation?.reclaimable_mb)}
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-200 truncate">
            InnoDB free pages
          </div>
        </div>

        {/* KPI 3: Tables */}
        <div className="p-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="text-[11px] font-medium text-gray-500 dark:text-gray-200 flex items-center justify-between">
            <span>Tables</span>
            <Layers className="w-3.5 h-3.5 text-blue-500" />
          </div>
          <div className="text-xl font-bold text-gray-900 dark:text-white font-mono tracking-tight my-1">
            {formatNumber(kpis?.tables)}
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-200 truncate">
            {engines.map((e) => `${e.tables} ${e.engine}`).join(' · ') || 'Total tables'}
          </div>
        </div>

        {/* KPI 4: Indexes */}
        <div className="p-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="text-[11px] font-medium text-gray-500 dark:text-gray-200 flex items-center justify-between">
            <span>Indexes</span>
            <TableOfContents className="w-3.5 h-3.5 text-amber-500" />
          </div>
          <div className="text-xl font-bold text-gray-900 dark:text-white font-mono tracking-tight my-1">
            {formatNumber(kpis?.indexes ?? indexes?.total)}
          </div>
          <div className="text-[11px] text-amber-600 dark:text-amber-400 truncate">
            {indexes?.unused ?? 0} unused · {indexes?.redundant ?? 0} redundant
          </div>
        </div>

        {/* KPI 5: Table without PK */}
        <div className="p-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="text-[11px] font-medium text-gray-500 dark:text-gray-200 flex items-center justify-between">
            <span>Table without PK</span>
            <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
          </div>
          <div className="text-xl font-bold text-amber-600 dark:text-amber-400 font-mono tracking-tight my-1">
            {formatNumber(tablesWithoutPk?.count ?? 0)}
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-200 truncate">
            {tablesWithoutPk?.list?.[0]
              ? `${tablesWithoutPk.list[0].database}.${tablesWithoutPk.list[0].table}`
              : 'None detected'}
          </div>
        </div>

        {/* KPI 6: Temp tables on disk */}
        <div className="p-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="text-[11px] font-medium text-gray-500 dark:text-gray-200 flex items-center justify-between">
            <span>Temp tables disk</span>
            <Activity className={`w-3.5 h-3.5 ${isTempWarn ? 'text-amber-500' : 'text-blue-500'}`} />
          </div>
          <div className="text-xl font-bold text-gray-900 dark:text-white font-mono tracking-tight my-1">
            {formatPct(tempTablesPct)}
          </div>
          <div>
            <div className="text-[11px] text-gray-500 dark:text-gray-200 mb-1 flex justify-between">
              <span>threshold 25%</span>
              <span className={isTempWarn ? 'text-amber-500 font-semibold' : 'text-gray-200'}>
                {isTempWarn ? 'High' : 'Normal'}
              </span>
            </div>
            <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-500 ${isTempWarn ? 'bg-amber-500' : 'bg-blue-500'}`}
                style={{ width: `${Math.min(tempTablesPct, 100)}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          ROW 2: CHARTS GRID (MATCHING mariadb-storage.html & PostgresStorageView)
          ───────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3.5">
        {/* Chart 1: Storage by engine (Donut matching PostgresStorageView Line 1020-1060) */}
        <div className="lg:col-span-4 p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
              <Layers className="w-3.5 h-3.5 text-blue-500" />
              Storage by engine
            </h3>
            <span className="text-[11px] font-mono text-gray-500 dark:text-gray-200">
              {formatSize(kpis?.total_size_mb)}
            </span>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-4 my-auto">
            {/* Explicit pixel w-36 h-36 container matching PostgresStorageView line 1022 */}
            <div className="relative w-36 h-36 flex-shrink-0 flex items-center justify-center">
              <ResponsiveContainer width={144} height={144} minWidth={144} minHeight={144}>
                <PieChart width={144} height={144}>
                  <Pie
                    data={enginePieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={42}
                    outerRadius={62}
                    dataKey="value"
                  >
                    {enginePieData.map((entry, idx) => (
                      <Cell key={`cell-${idx}`} fill={entry.color} stroke="none" />
                    ))}
                  </Pie>
                  <RechartsTooltip
                    formatter={(val: any, name: any) => [
                      `${formatSize(val)} (${enginePieData.find((d) => d.name === name)?.percentage?.toFixed(1) || 0}%)`,
                      name,
                    ]}
                    contentStyle={chartTheme.tooltip}
                    itemStyle={chartTheme.tooltipItem}
                    labelStyle={chartTheme.tooltipLabel}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-xs font-bold text-gray-900 dark:text-white font-mono leading-tight">
                  {formatSize(kpis?.total_size_mb)}
                </span>
                <span className="text-[10px] text-gray-500 dark:text-gray-200">total</span>
              </div>
            </div>

            {/* Engine list with percentage and table counts */}
            <div className="flex-1 w-full space-y-2 max-h-[160px] overflow-y-auto pr-1">
              {enginePieData.map((e) => (
                <div key={e.name} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2 truncate">
                    <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: e.color }} />
                    <span className="text-gray-800 dark:text-gray-200 truncate">{e.name}</span>
                  </div>
                  <span className="font-mono text-gray-500 dark:text-gray-200 text-[11px]">
                    {formatSize(e.displayVal)} ({e.percentage?.toFixed(1)}%)
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Chart 2: Footprint by database (horizontal Stacked Bar Chart) */}
        <div className="lg:col-span-8 p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
              <Database className="w-3.5 h-3.5 text-blue-500" />
              Footprint by database
            </h3>
            <span className="text-[11px] text-gray-500 dark:text-gray-200">stacked · data / index / reclaimable</span>
          </div>

          <div className="h-60 w-full flex-1" style={{ minHeight: '220px' }}>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart
                layout="vertical"
                data={dbFootprintData}
                margin={{ top: 10, right: 15, left: -10, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.grid} opacity={chartTheme.gridOpacity} horizontal={false} />
                <XAxis type="number" unit=" MB" tick={{ fontSize: 10, fill: chartTheme.axisText }} stroke={chartTheme.axisStroke} />
                <YAxis
                  type="category"
                  dataKey="database"
                  tick={{ fontSize: 10, fill: chartTheme.axisText }}
                  stroke={chartTheme.axisStroke}
                  width={85}
                  tickLine={false}
                  tickMargin={4}
                />
                <RechartsTooltip
                  formatter={(val: any, name: any) => [`${formatSize(Number(val))}`, name]}
                  contentStyle={chartTheme.tooltip}
                  itemStyle={chartTheme.tooltipItem}
                  labelStyle={chartTheme.tooltipLabel}
                  cursor={false}
                />
                <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '4px' }} iconSize={10} />
                <Bar dataKey="data_mb" name="Data" stackId="footprint" fill="#3b82f6" />
                <Bar dataKey="index_mb" name="Index" stackId="footprint" fill="#8b5cf6" />
                <Bar dataKey="free_mb" name="Reclaimable" stackId="footprint" fill="#f59e0b" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Chart 3: Largest tables (Data vs Index Vertical BarChart) */}
        <div className="lg:col-span-5 p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
              <Table className="w-3.5 h-3.5 text-blue-500" />
              Largest tables
            </h3>
            <span className="text-[11px] text-gray-500 dark:text-gray-200">data vs index · MB</span>
          </div>

          <div className="h-60 w-full flex-1" style={{ minHeight: '220px' }}>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart
                data={largestTablesChartData}
                margin={{ top: 10, right: 10, left: -10, bottom: 25 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.grid} opacity={chartTheme.gridOpacity} />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 10, fill: chartTheme.axisText }}
                  stroke={chartTheme.axisStroke}
                  angle={-20}
                  textAnchor="end"
                  interval={0}
                />
                <YAxis unit=" MB" tick={{ fontSize: 10, fill: chartTheme.axisText }} stroke={chartTheme.axisStroke} />
                <RechartsTooltip
                  formatter={(value: any, name: any) => [`${formatSize(Number(value))}`, name]}
                  labelFormatter={(label: any, payload: any) => payload?.[0]?.payload?.fullName || label}
                  contentStyle={chartTheme.tooltip}
                  itemStyle={chartTheme.tooltipItem}
                  labelStyle={chartTheme.tooltipLabel}
                  cursor={false}
                />
                <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '4px' }} iconSize={10} />
                <Bar dataKey="data_mb" name="Data MB" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                <Bar dataKey="index_mb" name="Index MB" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Chart 4: Row size vs row count (Bubble / Scatter) */}
        <div className="lg:col-span-4 p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
              <Activity className="w-3.5 h-3.5 text-cyan-500" />
              Row size vs row count
            </h3>
            <span className="text-[11px] text-gray-500 dark:text-gray-200">bubble · size = MB</span>
          </div>

          <div className="h-60 w-full flex-1" style={{ minHeight: '220px' }}>
            <ResponsiveContainer width="100%" height={220}>
              <ScatterChart margin={{ top: 10, right: 10, bottom: 10, left: -10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.grid} opacity={chartTheme.gridOpacity} />
                <XAxis
                  type="number"
                  dataKey="x"
                  name="Rows"
                  tick={{ fontSize: 10, fill: chartTheme.axisText }}
                  stroke={chartTheme.axisStroke}
                />
                <YAxis
                  type="number"
                  dataKey="y"
                  name="Avg row"
                  tick={{ fontSize: 10, fill: chartTheme.axisText }}
                  stroke={chartTheme.axisStroke}
                  unit=" B"
                />
                <ZAxis type="number" dataKey="z" range={[40, 260]} name="Data" unit=" MB" />
                <RechartsTooltip
                  cursor={{ strokeDasharray: '3 3', stroke: chartTheme.axisStroke }}
                  formatter={(val: any, name: any) => [name === 'Avg row' ? `${val} B` : formatNumber(val), name]}
                  labelFormatter={(_, payload: any) => payload?.[0]?.payload?.name || ''}
                  contentStyle={chartTheme.tooltip}
                  itemStyle={chartTheme.tooltipItem}
                  labelStyle={chartTheme.tooltipLabel}
                />
                <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '4px' }} iconSize={10} />
                {ariaScatterData.length > 0 && (
                  <Scatter name="Aria" data={ariaScatterData} fill="#06b6d4" />
                )}
                {innodbScatterData.length > 0 && (
                  <Scatter name="InnoDB" data={innodbScatterData} fill="#3b82f6" />
                )}
              </ScatterChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Chart 5: Capacity gauges vs thresholds (SVG semicircle gauge + mini specs) */}
        <div className="lg:col-span-3 p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
              <Gauge className="w-3.5 h-3.5 text-blue-500" />
              Capacity gauges
            </h3>
            <span className="text-[11px] text-gray-500 dark:text-gray-200">vs thresholds</span>
          </div>

          {/* SVG Semicircle Gauge matching PostgresStorageView */}
          <div className="relative flex flex-col items-center justify-center my-1">
            <svg viewBox="0 0 120 72" className="w-36 h-22 overflow-visible">
              <path
                d="M10 62A50 50 0 0 1 110 62"
                fill="none"
                stroke="currentColor"
                className="text-gray-200 dark:text-gray-700"
                strokeWidth="12"
                strokeLinecap="round"
                pathLength="100"
              />
              <path
                d="M10 62A50 50 0 0 1 110 62"
                fill="none"
                stroke={isTempWarn ? '#f59e0b' : '#3b82f6'}
                strokeWidth="12"
                strokeLinecap="round"
                pathLength="100"
                strokeDasharray={`${Math.min(tempTablesPct, 100)} 100`}
                className="transition-all duration-700 ease-out"
              />
              <path
                d="M10 62A50 50 0 0 1 110 62"
                fill="none"
                stroke="#f59e0b"
                strokeWidth="14"
                pathLength="100"
                strokeDasharray="0.8 100"
                strokeDashoffset="-25"
              />
              <text
                x="60"
                y="54"
                textAnchor="middle"
                className="text-base font-bold fill-gray-900 dark:fill-white font-mono"
              >
                {formatPct(tempTablesPct)}
              </text>
              <text
                x="60"
                y="67"
                textAnchor="middle"
                className="text-[9px] fill-gray-500 dark:fill-gray-400"
              >
                temp tables on disk
              </text>
            </svg>
          </div>

          {/* Mini spec grid matching prototype */}
          <div className="space-y-1.5 pt-2 border-t border-gray-100 dark:border-gray-700/60 text-xs">
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-200">Temp tables to disk</span>
              <span className="font-mono text-gray-800 dark:text-gray-200">
                {formatPct(tempTablesPct)} <small className="text-gray-200">(warn 25%)</small>
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-200">History list length</span>
              <span className="font-mono text-gray-800 dark:text-gray-200">
                {formatNumber(innodb?.history_list_length)} <small className="text-gray-200">(warn 1M)</small>
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-200">Auto-increment</span>
              <span className="font-mono text-gray-500 dark:text-gray-200">
                {kpis?.auto_increment_max_pct != null
                  ? `${formatPct(kpis.auto_increment_max_pct)}`
                  : 'unavailable'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-200">Binary log</span>
              <span className="font-mono text-gray-800 dark:text-gray-200">
                {binlog?.enabled ? 'enabled' : 'disabled'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-200">InnoDB system tablespace</span>
              <span className="font-mono text-gray-800 dark:text-gray-200">
                {formatSize(innodb?.system_tablespace_mb)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-200">File per table</span>
              <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                {innodb?.file_per_table ? 'on' : 'off'}
              </span>
            </div>
          </div>
        </div>

        {/* Chart 6: Database health radar (Recharts RadarChart) */}
        <div className="lg:col-span-4 p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-amber-500" />
              Database health radar
            </h3>
            <span className="text-[11px] text-gray-500 dark:text-gray-200">{radarSubjectDb?.database}</span>
          </div>

          <div className="h-60 w-full flex-1" style={{ minHeight: '220px' }}>
            <ResponsiveContainer width="100%" height={220}>
              <RadarChart cx="50%" cy="50%" outerRadius={68} data={radarChartData}>
                <PolarGrid stroke={chartTheme.radarWeb} strokeDasharray="3 3" opacity={isDark ? 0.65 : 0.9} gridType="polygon" />
                <PolarAngleAxis
                  dataKey="metric"
                  tick={{ fill: chartTheme.radarAngleText, fontSize: 10, fontWeight: 600 }}
                  stroke={chartTheme.radarWeb}
                />
                <PolarRadiusAxis
                  angle={30}
                  domain={[0, 'auto']}
                  stroke={chartTheme.radarWeb}
                  tick={{ fill: chartTheme.radarRadiusTick, fontSize: 9 }}
                />
                <RechartsTooltip
                  formatter={(val: any, name: any) => [`${val}`, name]}
                  contentStyle={chartTheme.tooltip}
                  itemStyle={chartTheme.tooltipItem}
                  labelStyle={chartTheme.tooltipLabel}
                />
                <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '4px' }} iconSize={10} />
                <Radar
                  name={radarSubjectDb?.database || 'Target DB'}
                  dataKey="target"
                  stroke="#f59e0b"
                  fill="#f59e0b"
                  fillOpacity={0.4}
                />
                <Radar
                  name="mysql (baseline)"
                  dataKey="baseline"
                  stroke="#3b82f6"
                  fill="#3b82f6"
                  fillOpacity={0.15}
                />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Chart 7: Tables per database (Pie Chart) */}
        <div className="lg:col-span-4 p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
              <Database className="w-3.5 h-3.5 text-violet-500" />
              Tables per database
            </h3>
            <span className="text-[11px] text-gray-500 dark:text-gray-200">distribution</span>
          </div>

          <div className="h-60 w-full flex-1" style={{ minHeight: '220px' }}>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={tablesPieData}
                  cx="50%"
                  cy="45%"
                  outerRadius={68}
                  dataKey="value"
                >
                  {tablesPieData.map((entry, idx) => (
                    <Cell key={`cell-${idx}`} fill={entry.color} stroke={chartTheme.pieStroke} strokeWidth={1.5} />
                  ))}
                </Pie>
                <RechartsTooltip
                  formatter={(val: any, name: any) => [
                    `${val} tables (${((Number(val) / (kpis?.tables || 1)) * 100).toFixed(1)}%)`,
                    name,
                  ]}
                  contentStyle={chartTheme.tooltip}
                  itemStyle={chartTheme.tooltipItem}
                  labelStyle={chartTheme.tooltipLabel}
                />
                <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '4px' }} iconSize={10} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="pt-2 border-t border-gray-100 dark:border-gray-700/60 text-[11px] text-gray-500 dark:text-gray-200 flex justify-between">
            <span>Total tables:</span>
            <span className="font-mono font-semibold text-gray-800 dark:text-gray-200">{kpis?.tables || 0}</span>
          </div>
        </div>

        {/* Chart 8: Index reads vs size (Horizontal BarChart) */}
        <div className="lg:col-span-4 p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center gap-1.5">
              <TableOfContents className="w-3.5 h-3.5 text-blue-500" />
              Index reads vs size
            </h3>
            <span className="text-[11px] text-gray-500 dark:text-gray-200">reads since start</span>
          </div>

          <div className="h-60 w-full flex-1" style={{ minHeight: '220px' }}>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart
                layout="vertical"
                data={indexReadsListData}
                margin={{ top: 10, right: 15, left: -5, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.grid} opacity={chartTheme.gridOpacity} horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10, fill: chartTheme.axisText }} stroke={chartTheme.axisStroke} />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fontSize: 10, fill: chartTheme.axisText }}
                  stroke={chartTheme.axisStroke}
                  width={78}
                  tickLine={false}
                  tickMargin={4}
                />
                <RechartsTooltip
                  formatter={(val: any, name: any) => [
                    name === 'reads' ? `${formatNumber(Number(val))} reads` : formatSize(Number(val)),
                    name === 'reads' ? 'Reads' : 'Size MB',
                  ]}
                  labelFormatter={(label: any, payload: any) => payload?.[0]?.payload?.fullName || label}
                  contentStyle={chartTheme.tooltip}
                  itemStyle={chartTheme.tooltipItem}
                  labelStyle={chartTheme.tooltipLabel}
                  cursor={false}
                />
                <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '4px' }} iconSize={10} />
                <Bar dataKey="reads" name="Reads" fill="#3b82f6" radius={[0, 4, 4, 0]}>
                  {indexReadsListData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.unused ? '#f59e0b' : '#3b82f6'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="pt-2 border-t border-gray-100 dark:border-gray-700/60 text-[11px] text-gray-500 dark:text-gray-200 flex items-center justify-between">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-blue-500" /> Active reads
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-amber-500" /> 0 reads (unused)
            </span>
          </div>
        </div>

        {/* Engine breakdown share progress bars */}
        <div className="lg:col-span-6 p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm space-y-3">
          <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center justify-between">
            <span>Engine breakdown</span>
            <span className="text-[11px] font-normal text-gray-200">share of total</span>
          </h3>

          <div className="space-y-3">
            {engines.map((e) => {
              const pct = e.size_pct ?? 0;
              const color = ENGINE_COLORS[e.engine] || '#3b82f6';
              return (
                <div key={e.engine} className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-semibold text-gray-800 dark:text-gray-200">{e.engine}</span>
                    <span className="text-gray-500 dark:text-gray-200 text-[11px] font-mono">
                      {formatSize(e.size_mb)} · {e.tables} tables ({pct.toFixed(1)}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                    <div
                      className="h-2 rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(100, Math.max(0, pct))}%`, backgroundColor: color }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Coverage & Diagnostics specs */}
        <div className="lg:col-span-6 p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm space-y-3">
          <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center justify-between">
            <span>Coverage</span>
            <span className="text-[11px] font-normal text-gray-200">telemetry specs</span>
          </h3>

          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="p-2.5 rounded-lg bg-gray-50 dark:bg-gray-900/50 border border-gray-100 dark:border-gray-700/60">
              <span className="text-[11px] text-gray-200">Databases checked</span>
              <div className="font-semibold text-gray-900 dark:text-white mt-0.5 font-mono">
                {coverage?.databases_checked ?? databases.length} / {coverage?.databases ?? databases.length}
              </div>
            </div>

            <div className="p-2.5 rounded-lg bg-gray-50 dark:bg-gray-900/50 border border-gray-100 dark:border-gray-700/60">
              <span className="text-[11px] text-gray-200">Persistent stats missing</span>
              <div className="font-semibold text-gray-900 dark:text-white mt-0.5 font-mono">
                {data?.statistics?.missing_persistent_stats ?? 0} tables
              </div>
            </div>

            <div className="p-2.5 rounded-lg bg-gray-50 dark:bg-gray-900/50 border border-gray-100 dark:border-gray-700/60">
              <span className="text-[11px] text-gray-200">Fragmented tables</span>
              <div className="font-semibold text-gray-900 dark:text-white mt-0.5 font-mono">
                {data?.fragmentation?.tables ?? 0}
              </div>
            </div>

            <div className="p-2.5 rounded-lg bg-gray-50 dark:bg-gray-900/50 border border-gray-100 dark:border-gray-700/60">
              <span className="text-[11px] text-gray-200">Free space in InnoDB</span>
              <div className="font-semibold text-gray-900 dark:text-white mt-0.5 font-mono">
                {formatSize(data?.fragmentation?.reclaimable_mb)}
              </div>
            </div>
          </div>

          {data?.auto_increment?.reason && (
            <div className="p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/20 text-xs text-amber-800 dark:text-amber-200">
              <span className="font-semibold text-amber-900 dark:text-amber-100">Visibility gap: </span>
              <span className="text-[11px] opacity-90">{data.auto_increment.reason}</span>
            </div>
          )}
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          ROW 3: INTERACTIVE DATA TABLES / TABS EXPLORER
          (Exactly matching PostgresStorageView.tsx Row 4 Style)
          ───────────────────────────────────────────────────────────── */}
      <div
        id="table-explorer-section"
        className="rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden"
      >
        {/* Navigation Sub-Tabs */}
        <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 px-4 pt-3 pb-0 bg-gray-50/50 dark:bg-gray-800/50">
          <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto">
            <button
              onClick={() => setActiveTableTab('inventory')}
              className={`px-3 py-2 text-xs font-semibold rounded-t-lg border-b-2 transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
                activeTableTab === 'inventory'
                  ? 'border-orange-500 text-orange-600 dark:text-orange-400 bg-white dark:bg-gray-800'
                  : 'border-transparent text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <Database className="w-3.5 h-3.5" />
              Database Inventory ({filteredDatabases.length})
            </button>

            <button
              onClick={() => setActiveTableTab('tables')}
              className={`px-3 py-2 text-xs font-semibold rounded-t-lg border-b-2 transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
                activeTableTab === 'tables'
                  ? 'border-orange-500 text-orange-600 dark:text-orange-400 bg-white dark:bg-gray-800'
                  : 'border-transparent text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <Table className="w-3.5 h-3.5" />
              Largest Tables ({filteredTables.length})
            </button>

            <button
              onClick={() => setActiveTableTab('indexes')}
              className={`px-3 py-2 text-xs font-semibold rounded-t-lg border-b-2 transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
                activeTableTab === 'indexes'
                  ? 'border-orange-500 text-orange-600 dark:text-orange-400 bg-white dark:bg-gray-800'
                  : 'border-transparent text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <TableOfContents className="w-3.5 h-3.5" />
              Index Inventory ({filteredIndexes.length})
            </button>

            <button
              onClick={() => setActiveTableTab('findings')}
              className={`px-3 py-2 text-xs font-semibold rounded-t-lg border-b-2 transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
                activeTableTab === 'findings'
                  ? 'border-orange-500 text-orange-600 dark:text-orange-400 bg-white dark:bg-gray-800'
                  : 'border-transparent text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <Sparkles className="w-3.5 h-3.5 text-amber-500" />
              Automated Findings ({findings?.count ?? filteredFindings.length})
            </button>

            {events.length > 0 && (
              <button
                onClick={() => setActiveTableTab('events')}
                className={`px-3 py-2 text-xs font-semibold rounded-t-lg border-b-2 transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
                  activeTableTab === 'events'
                    ? 'border-orange-500 text-orange-600 dark:text-orange-400 bg-white dark:bg-gray-800'
                    : 'border-transparent text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                <Clock className="w-3.5 h-3.5" />
                Recent Events ({events.length})
              </button>
            )}
          </div>
        </div>

        {/* ── SUB-TAB 1: DATABASE INVENTORY ── */}
        {activeTableTab === 'inventory' && (
          <div className="p-4 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="relative flex-1 max-w-sm">
                <Search className="w-3.5 h-3.5 text-gray-200 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Filter database name or status..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-white dark:bg-gray-900 text-xs text-gray-800 dark:text-gray-200 pl-8 pr-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 focus:outline-none focus:ring-1 focus:ring-orange-500 shadow-sm"
                />
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-200">
                Showing <span className="font-semibold text-gray-800 dark:text-gray-200">{filteredDatabases.length}</span> database(s)
              </div>
            </div>

            <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-200 font-semibold">
                  <th className="py-2.5 px-3 font-semibold">Database</th>
                  <th className="py-2.5 px-3 font-semibold">Status</th>
                  <th className="py-2.5 px-3 font-semibold text-right">Size</th>
                  <th className="py-2.5 px-3 font-semibold text-right">Data</th>
                  <th className="py-2.5 px-3 font-semibold text-right">Index</th>
                  <th className="py-2.5 px-3 font-semibold text-right">Free</th>
                  <th className="py-2.5 px-3 font-semibold text-right">Tables</th>
                  <th className="py-2.5 px-3 font-semibold text-right">Views</th>
                  <th className="py-2.5 px-3 font-semibold text-right">Est. Rows</th>
                  <th className="py-2.5 px-3 font-semibold">Engines</th>
                  <th className="py-2.5 px-3 font-semibold">Breakdown</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50 font-mono text-[11px]">
                {filteredDatabases.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="py-8 text-center text-gray-500 dark:text-gray-200">
                      No databases match the filter
                    </td>
                  </tr>
                ) : (
                  filteredDatabases.map((db, idx) => {
                    const isExpanded = expandedDb === db.database;
                    const statusColor =
                      db.status === 'healthy'
                        ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/30'
                        : 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400 border-amber-200 dark:border-amber-500/30';

                    return (
                      <React.Fragment key={idx}>
                        <tr className="hover:bg-gray-50/80 dark:hover:bg-gray-700/40 transition-colors">
                          <td className="py-2 px-3 font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            <span>{db.database}</span>
                            {db.is_system && (
                              <span className="px-1.5 py-0.5 rounded text-[9px] uppercase font-semibold bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                                system
                              </span>
                            )}
                            <button
                              onClick={() => handleCopy(db.database)}
                              className="text-gray-200 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                              title="Copy database name"
                            >
                              {copiedText === db.database ? (
                                <Check className="w-3 h-3 text-emerald-500" />
                              ) : (
                                <Copy className="w-3 h-3" />
                              )}
                            </button>
                          </td>
                          <td className="py-2 px-3 font-sans">
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${statusColor}`}>
                              {db.status || 'healthy'}
                            </span>
                          </td>
                          <td className="py-2 px-3 text-right font-bold text-gray-900 dark:text-white">
                            {formatSize(db.size_mb)}
                          </td>
                          <td className="py-2 px-3 text-right text-gray-600 dark:text-gray-300">
                            {formatSize(db.data_mb)}
                          </td>
                          <td className="py-2 px-3 text-right text-gray-600 dark:text-gray-300">
                            {formatSize(db.index_mb)}
                          </td>
                          <td className="py-2 px-3 text-right font-semibold text-amber-600 dark:text-amber-400">
                            {formatSize(db.free_mb)}
                          </td>
                          <td className="py-2 px-3 text-right text-gray-700 dark:text-gray-300 font-sans">
                            {formatNumber(db.tables)}
                          </td>
                          <td className="py-2 px-3 text-right text-gray-200 font-sans">
                            {formatNumber(db.views)}
                          </td>
                          <td className="py-2 px-3 text-right text-gray-700 dark:text-gray-300 font-sans">
                            {formatNumber(db.row_estimate)}
                          </td>
                          <td className="py-2 px-3 font-sans">
                            <div className="flex items-center gap-1 flex-wrap">
                              {db.engines?.map((eng) => (
                                <span
                                  key={eng.engine}
                                  className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                                >
                                  {eng.engine} ({eng.tables})
                                </span>
                              )) || '—'}
                            </div>
                          </td>
                          <td className="py-2 px-3 font-sans">
                            {db.engines && db.engines.length > 0 && (
                              <button
                                onClick={() => setExpandedDb(isExpanded ? null : db.database)}
                                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 cursor-pointer"
                                title="View engine breakdown"
                              >
                                <ChevronDown
                                  className={`w-3.5 h-3.5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                />
                              </button>
                            )}
                          </td>
                        </tr>

                        {/* Expandable engine details row */}
                        {isExpanded && db.engines && db.engines.length > 0 && (
                          <tr className="bg-gray-50/70 dark:bg-gray-900/40">
                            <td colSpan={11} className="p-3">
                              <div className="space-y-2">
                                <div className="text-[11px] font-bold text-gray-700 dark:text-gray-300 font-sans">
                                  Engine Breakdown for {db.database}:
                                </div>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                  {db.engines.map((e) => (
                                    <div
                                      key={e.engine}
                                      className="p-2 rounded bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-xs font-mono"
                                    >
                                      <div className="font-bold text-blue-600 dark:text-blue-400 font-sans">
                                        {e.engine}
                                      </div>
                                      <div className="text-[10px] text-gray-500 mt-1">
                                        Tables: {e.tables} · Rows: {formatNumber(e.est_rows)}
                                      </div>
                                      <div className="text-[10px] text-gray-500">
                                        Data: {formatSize(e.data_mb)} · Index: {formatSize(e.index_mb)}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                                {db.status_reasons && db.status_reasons.length > 0 && (
                                  <div className="pt-1 flex items-center gap-2 flex-wrap font-sans">
                                    <span className="text-[11px] font-semibold text-gray-500">Alerts:</span>
                                    {db.status_reasons.map((r, i) => (
                                      <span
                                        key={i}
                                        className="px-2 py-0.5 rounded text-[10px] bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800"
                                      >
                                        {r.reason}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

        {/* ── SUB-TAB 2: LARGEST TABLES ── */}
        {activeTableTab === 'tables' && (
          <div className="p-4 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="relative flex-1 max-w-sm">
                <Search className="w-3.5 h-3.5 text-gray-200 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Filter table name, database or format..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-white dark:bg-gray-900 text-xs text-gray-800 dark:text-gray-200 pl-8 pr-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 focus:outline-none focus:ring-1 focus:ring-orange-500 shadow-sm"
                />
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-gray-500 dark:text-gray-200">Engine:</span>
                  <select
                    value={tableEngineFilter}
                    onChange={(e) => setTableEngineFilter(e.target.value)}
                    className="px-2.5 py-1 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg text-xs text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-orange-500 shadow-sm"
                  >
                    <option value="all">All Engines</option>
                    <option value="InnoDB">InnoDB</option>
                    <option value="Aria">Aria</option>
                    <option value="CSV">CSV</option>
                  </select>
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-200">
                  Showing <span className="font-semibold text-gray-800 dark:text-gray-200">{filteredTables.length}</span> table(s)
                </div>
              </div>
            </div>

            <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-200 font-semibold">
                  <th className="py-2.5 px-3 font-semibold">Table</th>
                  <th className="py-2.5 px-3 font-semibold">Database</th>
                  <th className="py-2.5 px-3 font-semibold">Engine</th>
                  <th className="py-2.5 px-3 font-semibold text-right">Data</th>
                  <th className="py-2.5 px-3 font-semibold text-right">Index</th>
                  <th className="py-2.5 px-3 font-semibold text-right">Free</th>
                  <th className="py-2.5 px-3 font-semibold text-right">Est. Rows</th>
                  <th className="py-2.5 px-3 font-semibold text-right">Avg Row</th>
                  <th className="py-2.5 px-3 font-semibold">Format</th>
                  <th className="py-2.5 px-3 font-semibold">Auto Inc</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50 font-mono text-[11px]">
                {filteredTables.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="py-8 text-center text-gray-500 dark:text-gray-200">
                      No tables match the filter
                    </td>
                  </tr>
                ) : (
                  filteredTables.map((t, idx) => (
                    <tr key={idx} className="hover:bg-gray-50/80 dark:hover:bg-gray-700/40 transition-colors">
                      <td className="py-2 px-3 font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <span>{t.table}</span>
                        <button
                          onClick={() => handleCopy(t.table)}
                          className="text-gray-200 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                          title="Copy table name"
                        >
                          {copiedText === t.table ? (
                            <Check className="w-3 h-3 text-emerald-500" />
                          ) : (
                            <Copy className="w-3 h-3" />
                          )}
                        </button>
                      </td>
                      <td className="py-2 px-3 text-gray-600 dark:text-gray-300">
                        {t.database}
                      </td>
                      <td className="py-2 px-3 font-sans">
                        <span
                          className="px-2 py-0.5 rounded text-[10px] font-medium"
                          style={{
                            backgroundColor: `${ENGINE_COLORS[t.engine] || '#3b82f6'}15`,
                            color: ENGINE_COLORS[t.engine] || '#3b82f6',
                          }}
                        >
                          {t.engine}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-right font-bold text-gray-900 dark:text-white">
                        {formatSize(t.data_mb)}
                      </td>
                      <td className="py-2 px-3 text-right text-gray-600 dark:text-gray-300">
                        {formatSize(t.index_mb)}
                      </td>
                      <td className="py-2 px-3 text-right font-semibold text-amber-600 dark:text-amber-400">
                        {formatSize(t.free_mb)}
                      </td>
                      <td className="py-2 px-3 text-right text-gray-700 dark:text-gray-300 font-sans">
                        {formatNumber(t.est_rows)}
                      </td>
                      <td className="py-2 px-3 text-right text-gray-500 font-sans">
                        {formatNumber(t.avg_row_bytes)} B
                      </td>
                      <td className="py-2 px-3 text-gray-500 font-sans">
                        {t.row_format || '—'}
                      </td>
                      <td className="py-2 px-3 text-gray-600 dark:text-gray-300 font-sans">
                        {t.auto_increment ? formatNumber(t.auto_increment) : '—'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

        {/* ── SUB-TAB 3: INDEX INVENTORY ── */}
        {activeTableTab === 'indexes' && (
          <div className="p-4 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="relative flex-1 max-w-sm">
                <Search className="w-3.5 h-3.5 text-gray-200 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Filter table, index or column name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-white dark:bg-gray-900 text-xs text-gray-800 dark:text-gray-200 pl-8 pr-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 focus:outline-none focus:ring-1 focus:ring-orange-500 shadow-sm"
                />
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-900 p-0.5 rounded-lg text-xs">
                  <button
                    onClick={() => setIndexFilter('all')}
                    className={`px-2.5 py-1 rounded-md font-medium transition-colors cursor-pointer ${
                      indexFilter === 'all'
                        ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                        : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    All
                  </button>
                  <button
                    onClick={() => setIndexFilter('unused')}
                    className={`px-2.5 py-1 rounded-md font-medium transition-colors cursor-pointer ${
                      indexFilter === 'unused'
                        ? 'bg-white dark:bg-gray-700 text-amber-600 dark:text-amber-400 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    Unused ({indexes?.unused ?? 0})
                  </button>
                  <button
                    onClick={() => setIndexFilter('redundant')}
                    className={`px-2.5 py-1 rounded-md font-medium transition-colors cursor-pointer ${
                      indexFilter === 'redundant'
                        ? 'bg-white dark:bg-gray-700 text-rose-600 dark:text-rose-400 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    Redundant ({indexes?.redundant ?? 0})
                  </button>
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-200">
                  Showing <span className="font-semibold text-gray-800 dark:text-gray-200">{filteredIndexes.length}</span> index(es)
                </div>
              </div>
            </div>

            <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-200 font-semibold">
                  <th className="py-2.5 px-3 font-semibold">Table</th>
                  <th className="py-2.5 px-3 font-semibold">Index</th>
                  <th className="py-2.5 px-3 font-semibold">Columns</th>
                  <th className="py-2.5 px-3 font-semibold text-right">Cardinality</th>
                  <th className="py-2.5 px-3 font-semibold text-right">Reads</th>
                  <th className="py-2.5 px-3 font-semibold text-right">Writes</th>
                  <th className="py-2.5 px-3 font-semibold text-right">Size</th>
                  <th className="py-2.5 px-3 font-semibold">Flag</th>
                  <th className="py-2.5 px-3 font-semibold">Remediation Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50 font-mono text-[11px]">
                {filteredIndexes.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="py-8 text-center text-gray-500 dark:text-gray-200">
                      No indexes match the filter
                    </td>
                  </tr>
                ) : (
                  filteredIndexes.map((ix, idx) => {
                    const copyCmd =
                      ix.flag === 'unused'
                        ? `ALTER TABLE \`${ix.database || 'shop_demo'}\`.\`${ix.table}\` ALTER INDEX \`${ix.index}\` IGNORED;`
                        : ix.flag === 'redundant'
                          ? `ALTER TABLE \`${ix.database || 'shop_demo'}\`.\`${ix.table}\` DROP INDEX \`${ix.index}\`;`
                          : null;

                    return (
                      <tr key={idx} className="hover:bg-gray-50/80 dark:hover:bg-gray-700/40 transition-colors">
                        <td className="py-2 px-3 font-semibold text-gray-900 dark:text-white">
                          {ix.table}
                        </td>
                        <td className="py-2 px-3 text-gray-800 dark:text-gray-200">
                          {ix.index}
                        </td>
                        <td className="py-2 px-3 text-gray-500 font-sans max-w-xs truncate">
                          {Array.isArray(ix.columns) ? ix.columns.join(', ') : ix.columns || '—'}
                        </td>
                        <td className="py-2 px-3 text-right text-gray-700 dark:text-gray-300 font-sans">
                          {formatNumber(ix.cardinality)}
                        </td>
                        <td className="py-2 px-3 text-right font-bold text-gray-900 dark:text-white font-sans">
                          {formatNumber(ix.reads)}
                        </td>
                        <td className="py-2 px-3 text-right text-gray-500 font-sans">
                          {formatNumber(ix.writes)}
                        </td>
                        <td className="py-2 px-3 text-right text-gray-600 dark:text-gray-300">
                          {formatSize(ix.size_mb)}
                        </td>
                        <td className="py-2 px-3 font-sans">
                          {ix.flag === 'unused' ? (
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-300 dark:border-amber-500/30">
                              unused
                            </span>
                          ) : ix.flag === 'redundant' ? (
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-400 border border-rose-300 dark:border-rose-500/30">
                              redundant
                            </span>
                          ) : ix.unique ? (
                            <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-500/20">
                              unique
                            </span>
                          ) : (
                            <span className="text-gray-200 text-[10px]">—</span>
                          )}
                        </td>
                        <td className="py-2 px-3 font-sans">
                          {copyCmd && (
                            <button
                              onClick={() => handleCopy(copyCmd)}
                              className="px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 transition-colors flex items-center gap-1 text-[10px] cursor-pointer"
                              title="Copy SQL remediation command"
                            >
                              {copiedText === copyCmd ? (
                                <>
                                  <Check className="w-3 h-3 text-emerald-500" />
                                  <span className="text-emerald-600 dark:text-emerald-400">Copied</span>
                                </>
                              ) : (
                                <>
                                  <Copy className="w-3 h-3" />
                                  <span>Copy {ix.flag === 'unused' ? 'IGNORE' : 'DROP'}</span>
                                </>
                              )}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

        {/* ── SUB-TAB 4: AUTOMATED FINDINGS ── */}
        {activeTableTab === 'findings' && (
          <div className="p-4 space-y-4">
            {findings?.note && (
              <div className="p-3.5 rounded-xl bg-amber-500/10 border border-amber-500/20 text-xs text-amber-800 dark:text-amber-200 flex items-start gap-2.5">
                <Info className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <span className="font-semibold text-amber-900 dark:text-amber-100">Advisory Notice:</span>
                  <p className="leading-relaxed text-[11px] opacity-90">{findings.note}</p>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="relative flex-1 max-w-sm">
                <Search className="w-3.5 h-3.5 text-gray-200 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Filter findings by text, object or category..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-white dark:bg-gray-900 text-xs text-gray-800 dark:text-gray-200 pl-8 pr-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 focus:outline-none focus:ring-1 focus:ring-orange-500 shadow-sm"
                />
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {findings?.by_severity &&
                  Object.entries(findings.by_severity).map(([sev, count]) => {
                    const color =
                      sev === 'critical'
                        ? 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20'
                        : sev === 'warning'
                          ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20'
                          : 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20';
                    return (
                      <span
                        key={sev}
                        className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${color}`}
                      >
                        {count} {sev}
                      </span>
                    );
                  })}
                <span className="text-xs text-gray-500 dark:text-gray-200">
                  Showing <span className="font-semibold text-gray-800 dark:text-gray-200">{filteredFindings.length}</span> of {findings?.count ?? filteredFindings.length}
                </span>
              </div>
            </div>

            {filteredFindings.length === 0 ? (
              <div className="py-12 text-center text-xs text-gray-500 dark:text-gray-200">
                No automated findings matching the current filters.
              </div>
            ) : (
              <div className="space-y-3">
                {filteredFindings.map((item, idx) => {
                  const sevColor =
                    item.severity === 'critical'
                      ? 'bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-400 border-rose-200 dark:border-rose-500/30'
                      : item.severity === 'warning'
                        ? 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400 border-amber-200 dark:border-amber-500/30'
                        : 'bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-400 border-blue-200 dark:border-blue-500/30';

                  return (
                    <div
                      key={idx}
                      className="p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/80 shadow-sm hover:border-gray-300 dark:hover:border-gray-600 transition-all space-y-3"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span
                            className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${sevColor}`}
                          >
                            {item.severity}
                          </span>
                          <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                            {item.category?.replace(/_/g, ' ')}
                          </span>
                          {item.database && (
                            <span className="px-2 py-0.5 rounded text-[10px] font-mono font-medium bg-orange-50 dark:bg-orange-500/10 text-orange-700 dark:text-orange-400 border border-orange-200 dark:border-orange-500/20">
                              {item.database}
                            </span>
                          )}
                        </div>
                        {item.object && (
                          <span className="font-mono text-xs font-semibold text-gray-700 dark:text-gray-300 truncate max-w-md">
                            {item.object}
                          </span>
                        )}
                      </div>

                      <div className="space-y-1.5 text-xs">
                        <div className="text-gray-900 dark:text-white font-medium leading-relaxed">
                          {item.finding}
                        </div>
                        {item.impact && (
                          <div className="text-gray-600 dark:text-gray-300 text-[11px] flex items-start gap-1.5">
                            <span className="font-semibold text-gray-500 dark:text-gray-200 min-w-[55px]">
                              Impact:
                            </span>
                            <span>{item.impact}</span>
                          </div>
                        )}
                        {item.remediation && (
                          <div className="text-gray-600 dark:text-gray-300 text-[11px] flex items-start gap-1.5">
                            <span className="font-semibold text-blue-600 dark:text-blue-400 min-w-[55px]">
                              Remedy:
                            </span>
                            <span>{item.remediation}</span>
                          </div>
                        )}
                      </div>

                      {item.command && (
                        <div className="pt-1">
                          <div className="flex items-center justify-between bg-gray-900 text-gray-100 dark:bg-black/60 rounded-lg p-2.5 font-mono text-[11px] border border-gray-700/60 overflow-x-auto">
                            <div className="flex items-center gap-2 flex-1 min-w-0 pr-2">
                              <Terminal className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                              <span className="truncate select-all">{item.command}</span>
                            </div>
                            <button
                              onClick={() => handleCopy(item.command || '')}
                              className="px-2 py-1 rounded bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white transition-colors flex items-center gap-1 text-[10px] font-medium shrink-0 cursor-pointer"
                              title="Copy command to clipboard"
                            >
                              {copiedText === item.command ? (
                                <>
                                  <Check className="w-3 h-3 text-emerald-400" />
                                  <span className="text-emerald-400">Copied</span>
                                </>
                              ) : (
                                <>
                                  <Copy className="w-3 h-3" />
                                  <span>Copy</span>
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── SUB-TAB 5: ALERTS & EVENTS ── */}
        {activeTableTab === 'events' && (
          <div className="p-4 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="relative flex-1 max-w-sm">
                <Search className="w-3.5 h-3.5 text-gray-200 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Filter events..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-white dark:bg-gray-900 text-xs text-gray-800 dark:text-gray-200 pl-8 pr-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 focus:outline-none focus:ring-1 focus:ring-orange-500 shadow-sm"
                />
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-200">
                Total Events: <span className="font-semibold text-gray-800 dark:text-gray-200">{filteredEvents.length}</span>
              </div>
            </div>

            {filteredEvents.length === 0 ? (
              <div className="py-12 text-center text-xs text-gray-500 dark:text-gray-200">
                No maintenance alerts or events recorded
              </div>
            ) : (
              <div className="space-y-2">
                {filteredEvents.map((ev, i) => (
                  <div
                    key={i}
                    className="p-3.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/80 flex items-start gap-3 shadow-sm"
                  >
                    <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-gray-900 dark:text-white">{ev.event}</span>
                        {ev.database && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-orange-50 dark:bg-orange-500/10 text-orange-700 dark:text-orange-400 border border-orange-200 dark:border-orange-500/20">
                            {ev.database}
                          </span>
                        )}
                        {ev.time && (
                          <span className="text-[10px] text-gray-200 ml-auto font-mono">
                            {formatDateTime(ev.time)}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-600 dark:text-gray-300 mt-1 leading-relaxed">
                        {ev.details}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export const MariaDbStorageView = MariaDbStorage;
export default MariaDbStorage;
